import { Fragment, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Container } from "@/components/ui/Container";
import { StoreCard } from "@/components/ui/StoreCard";
import { Typography } from "@/components/ui/Typography";

export interface StoresSectionStoreLink {
	label: string;
	href: string;
	variant?: "external" | "phone";
	target?: "_blank" | "_self";
}

export interface StoresSectionStore {
	id: string;
	addressLines: string[];
	hours?: string[];
	links?: StoresSectionStoreLink[];
	image?: { src: string; alt?: string };
}

export interface StoresSectionProps {
	caption?: string;
	heading?: string;
	stores: StoresSectionStore[];
	className?: string;
}

const renderAddress = (lines: string[]): ReactNode =>
	lines.map((line, i) => (
		<Fragment key={`${line}-${i}`}>
			{i > 0 && <br />}
			{line}
		</Fragment>
	));

export const StoresSection = ({ caption, heading, stores, className }: StoresSectionProps) => {
	if (stores.length === 0) return null;

	return (
		<section className={twMerge(className)}>
			<Container className="py-grid-section-vertical-lg">
				<div className="flex flex-col gap-grid-section-vertical-md lg:grid lg:grid-cols-12 lg:gap-x-grid-grid-gutter-md lg:gap-y-0">
					<div className="flex flex-col gap-grid-component-spacing-sm lg:col-span-3">
						{caption && (
							<Typography
								variant="label-sm-reg"
								as="p"
								className="leading-[1.15] text-text-dark-text-1"
							>
								{caption}
							</Typography>
						)}
						{heading && (
							<Typography variant="heading-display-sm" as="h2" className="text-text-dark-text-1">
								{heading}
							</Typography>
						)}
					</div>
					<div className="flex flex-col gap-grid-section-vertical-md lg:col-span-8 lg:col-start-5">
						{stores.map((store) => (
							<StoreCard
								key={store.id}
								address={renderAddress(store.addressLines)}
								hours={store.hours}
								links={store.links}
								image={store.image}
							/>
						))}
					</div>
				</div>
			</Container>
		</section>
	);
};
